import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { hasPermission, canPerformSegmentAction } from '../../utils/permissions';
import Button from '../common/Button'; // Assuming Button component is used

const SaveSegmentModal = ({ isOpen, onClose, filters, activeFilters, users = [], manualMode = false, onSave }) => {
  // Check segment permissions
  const canCreateOwnSegments = canPerformSegmentAction('create_own');
  const canCreatePublicSegments = canPerformSegmentAction('create_public');

  // Redirect if user doesn't have permission (only relevant if opened directly, not via SegmentsListModal which should handle this)
  useEffect(() => {
    if (isOpen && !canCreateOwnSegments) {
      onClose();
      // Optionally show an alert or notification
      // alert('You do not have permission to create segments.');
    }
  }, [isOpen, canCreateOwnSegments, onClose]);

  const [segmentName, setSegmentName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Generate a suggested name based on active filters (only for dynamic mode)
  const generateSuggestedName = () => {
    if (manualMode || !activeFilters || Object.keys(activeFilters).length === 0) {
      return ''; // No suggestion for manual mode or if no filters
    }

    // Convert filter keys to readable names and format values
    const filterParts = Object.entries(activeFilters).map(([key, value]) => {
      // Special case for courses_bought - rename to Associated Deals
      let readableKey;
      if (key === 'courses_bought') {
        readableKey = 'Associated Deals';
      } else {
        readableKey = key.replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }

      if (key === 'assigned_to' && users.length > 0) {
        const user = users.find(user => user.id === value || user.id === parseInt(value));
        const displayValue = user ? user.name : value;
        return `${readableKey}: ${displayValue}`;
      }

      return `${readableKey}: ${value}`;
    });

    return filterParts.join(', ');
  };

  // Reset form and generate suggested name when modal opens
  useEffect(() => {
    if (isOpen) {
      const suggestedName = !manualMode ? generateSuggestedName() : '';
      setSegmentName(suggestedName);
      setDescription('');
      setIsPublic(false); // Default to private
      setError('');
      setSuccess(false);
    }
  }, [isOpen, activeFilters, manualMode]); // Add manualMode dependency

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!segmentName.trim()) {
      setError('Please enter a segment name');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      // Prepare data based on mode
      const segmentData = {
        name: segmentName,
        description,
        is_public: isPublic,
      };

      if (manualMode) {
        segmentData.is_dynamic = false;
        segmentData.filter_config = null;
        // Call the onSave prop passed from SegmentsListModal for manual creation
        if (typeof onSave === 'function') {
            await onSave(segmentData); // Use the passed onSave handler
        } else {
            throw new Error("onSave handler not provided for manual mode");
        }
      } else {
        // Dynamic segment creation (existing logic)
        segmentData.is_dynamic = true;
        segmentData.filter_config = { ...activeFilters }; // Use active filters

        // Make API request directly for dynamic segments
        await axios.post('/api/segments', segmentData, {
          headers: {
            'x-auth-token': token
          }
        });
      }

      setSuccess(true);

      // Close modal after short delay to show success message
      setTimeout(() => {
        onClose(); // Close this modal
      }, 1500);

    } catch (err) {
      console.error('Error saving segment:', err);
      setError(err.response?.data?.message || 'Failed to save segment');
    } finally {
      setLoading(false);
    }
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {manualMode ? 'Create Manual Segment' : 'Save Filter as Segment'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {manualMode
                        ? 'Create a new empty segment to manually add leads to later.'
                        : 'Save your current filter settings as a dynamic segment for quick access later.'
                      }
                    </p>

                    {error && (
                      <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-400 text-red-700">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="mt-2 p-2 bg-green-50 border-l-4 border-green-400 text-green-700">
                        Segment saved successfully!
                      </div>
                    )}

                    <div className="mt-4">
                      <label htmlFor="segment-name" className="block text-sm font-medium text-gray-700">
                        Segment Name *
                      </label>
                      <input
                        type="text"
                        id="segment-name"
                        value={segmentName}
                        onChange={(e) => setSegmentName(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter a name for this segment"
                        required
                      />
                    </div>

                    <div className="mt-4">
                      <label htmlFor="segment-description" className="block text-sm font-medium text-gray-700">
                        Description (optional)
                      </label>
                      <textarea
                        id="segment-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows="2"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Add a description for this segment"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Who can access this segment?
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            id="private-segment"
                            name="visibility"
                            type="radio"
                            checked={!isPublic}
                            onChange={() => setIsPublic(false)}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                          />
                          <label htmlFor="private-segment" className="ml-3 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <div>
                              <span className="block text-sm font-medium text-gray-700">Only me</span>
                              <span className="block text-xs text-gray-500">This segment will only be visible to you</span>
                            </div>
                          </label>
                        </div>
                        {canCreatePublicSegments && (
                          <div className="flex items-center">
                            <input
                              id="public-segment"
                              name="visibility"
                              type="radio"
                              checked={isPublic}
                              onChange={() => setIsPublic(true)}
                              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <label htmlFor="public-segment" className="ml-3 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                              </svg>
                              <div>
                                <span className="block text-sm font-medium text-gray-700">All users</span>
                                <span className="block text-xs text-gray-500">Everyone will be able to see and use this segment</span>
                              </div>
                            </label>
                          </div>
                        )}
                        {!canCreatePublicSegments && (
                          <div className="mt-1 text-xs text-gray-500 italic">
                            You don't have permission to create public segments that are visible to all users.
                            Your segment will only be visible to you.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Conditionally render Active Filters section */}
                    {!manualMode && activeFilters && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700">Active Filters Being Saved:</h4>
                        <div className="mt-1 bg-gray-50 p-2 rounded border border-gray-200 max-h-32 overflow-y-auto">
                          {Object.keys(activeFilters).length > 0 ? (
                            <ul className="text-xs text-gray-600">
                              {Object.entries(activeFilters).map(([key, value]) => (
                                <li key={key} className="mb-1">
                                  <span className="font-medium">
                                    {key === 'courses_bought' ? 'associated deals' : key.replace(/_/g, ' ')}:
                                  </span>{' '}
                                  {key === 'assigned_to' && users.length > 0 ? (
                                    (() => {
                                      const user = users.find(user => user.id === value || user.id === parseInt(value));
                                      return user ? user.name : value;
                                    })()
                                  ) : (
                                    value
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-gray-500">No active filters. The dynamic segment will include all leads.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Saving...' : (manualMode ? 'Create Segment' : 'Save Segment')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaveSegmentModal;
